/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { customTranslationDemoLink } from 'src/languageStudio/appShell/landingPage/utils/landingPageLinks';
import { headerClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import {
    documentTranslationDocLink,
    documentTranslationSampleLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';

const messages = defineMessages({
    option1: { id: 'languageStudio.documentTranslationDemoPage.option1', defaultMessage: 'Option 1:' },
    option2: { id: 'languageStudio.documentTranslationDemoPage.option2', defaultMessage: 'Option 2:' },
    sampleCode: { id: 'languageStudio.documentTranslationDemoPage.sampleCode', defaultMessage: 'sample code.' },
    quickStart: { id: 'languageStudio.documentTranslationDemoPage.quickStart ', defaultMessage: 'quick start' },
    optionTwoText: { id: 'languageStudio.documentTranslationDemoPage.optionTwoText', defaultMessage: 'Try creating a' },
    nextStepsSectionTitle: {
        id: 'languageStudio.documentTranslationDemoPage.nextStepsSectionTitle',
        defaultMessage: 'You optional next steps'
    },
    optionOneTextCont: {
        id: 'languageStudio.documentTranslationDemoPage.optionOneTextCont',
        defaultMessage: 'for code reference or get'
    },
    customTranslationProject: {
        id: 'languageStudio.documentTranslationDemoPage.customTranslationProject ',
        defaultMessage: 'custom translation project'
    },
    optionOneText: {
        id: 'languageStudio.documentTranslationDemoPage.optionOneText',
        defaultMessage: 'Set up your own document translation system. Go to'
    },
    optionTwoTextCont: {
        id: 'languageStudio.documentTranslationDemoPage.optionTwoTextCont',
        defaultMessage: `to further improve translation accuracy. You can tailor the translation model or create custom
        dictionary to meet your particular business needs with your previously translated in-domain documents.`
    }
});

export const DocumentTranslatorNextSteps = () => {
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            <EditableTitle
                className={headerClassName}
                title={localize(messages.nextStepsSectionTitle)}
                titleTextProps={{ variant: 'xLarge', fontWeight: 600 }}
            />

            <LuisText>
                <LuisText fontWeight={500}>{localize(messages.option1)}</LuisText>
                &nbsp;
                {localize(messages.optionOneText)}
                &nbsp;
                <ExternalLink href={documentTranslationDocLink}>{localize(messages.quickStart)}</ExternalLink>
                &nbsp;
                {localize(messages.optionOneTextCont)}
                &nbsp;
                <ExternalLink href={documentTranslationSampleLink}>{localize(messages.sampleCode)}</ExternalLink>
            </LuisText>

            <LuisText>
                <LuisText fontWeight={500}>{localize(messages.option2)}</LuisText>
                &nbsp;
                {localize(messages.optionTwoText)}
                &nbsp;
                <ExternalLink href={customTranslationDemoLink}>{localize(messages.customTranslationProject)}</ExternalLink>
                &nbsp;
                {localize(messages.optionTwoTextCont)}
            </LuisText>
        </Stack>
    );
};
